import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useCustomerAuth } from '@/contexts/CustomerAuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Shield, Loader2, LogOut } from 'lucide-react';
import { BrandedSpinner } from '@/components/ui/branded-spinner';
import paynovusLogoStacked from '@/assets/paynovus-logo-stacked-tagline.png';
import { AUTH_ONBOARDING_RESUME_KEY } from '@/constants/onboardingResume';
import { CUSTOMER_LOGIN_TYPE_MISMATCH_KEY } from '@/constants/customerAuthRouting';

// Module-level code-reuse guard
let lastMfaCode = '';
let lastMfaTimestamp = 0;
const MFA_REUSE_WINDOW_MS = 90_000;

export default function CustomerMFAChallenge() {
  const { setAuthGuard, loadProfileAfterMfa, signOut } = useCustomerAuth();
  const navigate = useNavigate();
  const [factorId, setFactorId] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [loading, setLoading] = useState(true);

  const loadFactor = useCallback(async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      navigate('/app/login');
      return;
    }

    // Recovery sessions (password reset tokens) cannot be used for MFA — sign out and re-login
    if (session.user?.aud === 'authenticated' && !session.user?.email) {
      await supabase.auth.signOut();
      navigate('/app/login');
      return;
    }

    // Check token has valid sub claim before attempting MFA
    const token = session.access_token;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      if (!payload.sub) {
        await supabase.auth.signOut();
        navigate('/app/login');
        return;
      }
    } catch {
      await supabase.auth.signOut();
      navigate('/app/login');
      return;
    }

    const { data } = await supabase.auth.mfa.listFactors();
    const factor = data?.totp?.find(f => f.status === 'verified');
    if (!factor) {
      navigate('/app/mfa-setup', { replace: true });
      return;
    }
    setFactorId(factor.id);
    setLoading(false);
  }, [navigate]);

  useEffect(() => {
    loadFactor();
  }, [loadFactor]);

  async function handleVerify() {
    // Prevent code reuse
    if (code === lastMfaCode && (Date.now() - lastMfaTimestamp) < MFA_REUSE_WINDOW_MS) {
      setError('This code has already been used. Please wait for a new code.');
      setCode('');
      return;
    }

    setError(null);
    setIsVerifying(true);

    try {
      // Re-validate session immediately before attempting challenge
      // (session can become invalid between loadFactor and verify)
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      if (!currentSession?.access_token) {
        await supabase.auth.signOut();
        navigate('/app/login', { replace: true });
        return;
      }
      // Verify sub claim is still present in the current token
      try {
        const payload = JSON.parse(atob(currentSession.access_token.split('.')[1]));
        if (!payload.sub) {
          await supabase.auth.signOut();
          navigate('/app/login', { replace: true });
          return;
        }
      } catch {
        await supabase.auth.signOut();
        navigate('/app/login', { replace: true });
        return;
      }

      // Set auth guard to suppress SIGNED_OUT events during MFA token rotation
      setAuthGuard(true);

      const { data: challenge, error: challengeErr } = await supabase.auth.mfa.challenge({ factorId });
      if (challengeErr) {
        setAuthGuard(false);
        setError(challengeErr.message);
        return;
      }

      const { error: verifyErr } = await supabase.auth.mfa.verify({
        factorId,
        challengeId: challenge.id,
        code,
      });

      if (verifyErr) {
        setAuthGuard(false);
        setError('Invalid code. Please try again.');
        setCode('');
        return;
      }

      lastMfaCode = code;
      lastMfaTimestamp = Date.now();
      setCode('');

      // Force session refresh so the new AAL2 token is in the SDK
      await supabase.auth.refreshSession();

      // Directly load profile — polls until AAL2 is confirmed, then fetches profile
      await loadProfileAfterMfa();

      const mismatch = localStorage.getItem(CUSTOMER_LOGIN_TYPE_MISMATCH_KEY);
      if (mismatch) {
        localStorage.removeItem(CUSTOMER_LOGIN_TYPE_MISMATCH_KEY);
        const label = mismatch === 'corporate' ? 'Business' : 'Personal';
        await signOut();
        navigate('/app/login', {
          replace: true,
          state: { error: `No ${label} account found for this email. Please select the correct account type or contact support.` },
        });
        return;
      }

      // Navigate to dashboard — profile is already loaded in context
      navigate(sessionStorage.getItem(AUTH_ONBOARDING_RESUME_KEY) ? '/app/onboard' : '/app', { replace: true });

      // Release guard after navigation
      setTimeout(() => setAuthGuard(false), 5_000);
    } catch {
      setAuthGuard(false);
      setError('Verification failed');
    } finally {
      setIsVerifying(false);
    }
  }

  async function handleSignOut() {
    await signOut();
    navigate('/app/login');
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <BrandedSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen-safe hero-dark flex flex-col items-center justify-center p-4 sm:p-6">
      <div className="w-full max-w-md space-y-6">
        <div className="text-center">
          <img src={paynovusLogoStacked} alt="PayNovus" className="h-20 sm:h-24 w-auto mx-auto mb-6" />
          <div className="flex items-center justify-center gap-2 mb-2">
            <Shield className="h-6 w-6 text-accent" />
            <h1 className="text-2xl font-bold text-foreground">Two-Factor Authentication</h1>
          </div>
          <p className="text-muted-foreground text-sm">
            Enter the code from your authenticator app to continue
          </p>
        </div>

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="mfa-code" className="text-foreground">Authentication Code</Label>
            <Input
              id="mfa-code"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              placeholder=""
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && code.length === 6) handleVerify();
              }}
              className="text-center text-2xl tracking-widest"
              autoFocus
              disabled={isVerifying}
            />
          </div>

          <Button
            className="w-full h-12 rounded-full text-base font-semibold"
            onClick={handleVerify}
            disabled={code.length !== 6 || isVerifying}
          >
            {isVerifying ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Verifying...
              </>
            ) : (
              'Verify'
            )}
          </Button>

          <button
            onClick={handleSignOut}
            className="w-full flex items-center justify-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors py-2"
          >
            <LogOut className="w-4 h-4" />
            Sign out
          </button>

          <div className="text-center pt-2 border-t border-border/30">
            <button
              type="button"
              onClick={() => navigate('/app/mfa-setup?reenroll=1', { replace: true })}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors underline underline-offset-2"
            >
              Lost access to your authenticator? Re-enroll here
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
